const RELOAD_FLAG_KEY = 'chunk-error-reload';
// Shared cooldown between ChunkErrorHandler (reactive: a chunk 404'd) and
// AppVersionCheckService (proactive: polling detected a new deploy) so the
// two mechanisms can't bounce the page back and forth off each other.
const RELOAD_COOLDOWN_MS = 60_000;

export class ReloadGuardHelper {
  private static _activeHolds = 0;

  /**
   * Blocks automatic reloads while a video call or stream is on screen.
   *
   * A reload is a full navigation: it closes the video room, drops the Vonage
   * session and restarts the HLS player from scratch. Users returning to the
   * tab after a deploy were being kicked out of calls by exactly that.
   *
   * Returns the release function; calling it more than once is harmless.
   */
  public static holdReload(): () => void {
    if (typeof window === 'undefined') {
      // Statics are shared by every request on the SSR server.
      return () => {};
    }

    ReloadGuardHelper._activeHolds++;
    let released = false;

    return () => {
      if (released) {
        return;
      }
      released = true;
      ReloadGuardHelper._activeHolds--;
    };
  }

  public static get isReloadHeld(): boolean {
    return ReloadGuardHelper._activeHolds > 0;
  }

  public static canReload(): boolean {
    if (ReloadGuardHelper.isReloadHeld) {
      return false;
    }

    if (typeof sessionStorage === 'undefined') {
      return true;
    }

    const lastReload = sessionStorage.getItem(RELOAD_FLAG_KEY);
    if (!lastReload) {
      return true;
    }

    return Date.now() - Number(lastReload) >= RELOAD_COOLDOWN_MS;
  }

  /**
   * Navigates to the current URL with a cache-busting param instead of
   * calling reload() so the browser treats it as a fresh request rather than
   * reusing anything cached under the current URL.
   */
  public static reloadToFreshUrl(): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(RELOAD_FLAG_KEY, String(Date.now()));
    }

    const url = new URL(window.location.href);
    url.searchParams.set('_cb', String(Date.now()));
    window.location.href = url.toString();
  }
}
